import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast, Toaster } from 'react-hot-toast';
import { useSearchParams } from 'react-router';
import axiosInstance from '../axiosInstance/axiosInstance';
import Pagination from '../components/pagination/pagination'; // Adjust path as needed

const CompaniesDetail = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page")) || 1;
  
  const [companyDetails, setCompanyDetails] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAltPhone, setShowAltPhone] = useState(false);
  const [showAltEmail, setShowAltEmail] = useState(false);

  // Pagination configuration
  const itemsPerPage = useMemo(() => 10, []); // Number of items per page
  const totalPages = useMemo(() => {
    return Math.ceil(companyDetails.length / itemsPerPage);
  }, [companyDetails, itemsPerPage]);

  // Get current page items
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage;
    return companyDetails.slice(startIndex, endIndex);
  }, [companyDetails, currentPage, itemsPerPage]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm();

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setSearchParams({ page: page });
    }
  };

  // Fetch all company details
  const fetchCompanyDetails = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/company-detail');
      if (response.data.status) {
        setCompanyDetails(response.data.companyDetails);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch company details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, []);

  // Handle form submission (Create/Update)
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Remove empty alternate fields if not provided
      if (!data.alternatePhone) delete data.alternatePhone;
      if (!data.alternateEmail) delete data.alternateEmail;
      if (!data.message) delete data.message;

      if (editingId) {
        const response = await axiosInstance.put(`/company-detail/${editingId}`, data);
        if (response.data.status) {
          toast.success('Company detail updated successfully');
          fetchCompanyDetails();
          resetForm();
        }
      } else {
        const response = await axiosInstance.post('/company-detail', data);
        if (response.data.companyDetail) {
          toast.success('Company detail created successfully');
          fetchCompanyDetails();
          resetForm();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (company) => {
    setEditingId(company._id);
    setIsFormOpen(true);
    
    // Set form values based on schema
    setValue('name', company.name);
    setValue('companyName', company.companyName);
    setValue('position', company.position);
    setValue('experience', company.experience);
    setValue('location', company.location);
    setValue('salary', company.salary);
    setValue('jobType', company.jobType);
    setValue('phone', company.phone);
    setValue('email', company.email);
    
    // Set optional fields if they exist
    if (company.alternatePhone) {
      setShowAltPhone(true);
      setValue('alternatePhone', company.alternatePhone);
    }
    if (company.alternateEmail) {
      setShowAltEmail(true);
      setValue('alternateEmail', company.alternateEmail);
    }
    if (company.message) {
      setValue('message', company.message);
    }
    
    // Scroll to form
    document.getElementById('company-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle delete
  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await axiosInstance.delete(`/company-detail/${id}`);
      if (response.data.status) {
        toast.success('Company detail deleted successfully');
        fetchCompanyDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    } finally {
      setLoading(false);
      setDeleteConfirm(null);
    }
  };

  // Reset form
  const resetForm = () => {
    reset({
      name: '',
      companyName: '',
      position: '',
      experience: '',
      location: '',
      salary: '',
      jobType: '',
      phone: '',
      email: '',
      alternatePhone: '',
      alternateEmail: '',
      message: '',
    });
    setEditingId(null);
    setIsFormOpen(false);
    setShowAltPhone(false);
    setShowAltEmail(false);
  };

  // Job type options
  const jobTypeOptions = [
    { value: 'Full-time', label: 'Full-time' },
    { value: 'Part-time', label: 'Part-time' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Internship', label: 'Internship' },
    { value: 'Remote', label: 'Remote' },
    { value: 'Hybrid', label: 'Hybrid' },
  ];

  // Experience options
  const experienceOptions = [
    { value: 'Fresher', label: 'Fresher (0-1 years)' },
    { value: '1-3 years', label: '1-3 years' },
    { value: '3-5 years', label: '3-5 years' },
    { value: '5-7 years', label: '5-7 years' },
    { value: '7-10 years', label: '7-10 years' },
    { value: '10+ years', label: '10+ years' },
  ];

  // Form fields based on your schema
  const formFields = [
    { name: 'name', label: 'Contact Person Name', type: 'text', required: true, placeholder: 'Enter contact person name', colSpan: 1 },
    { name: 'companyName', label: 'Company Name', type: 'text', required: true, placeholder: 'Enter company name', colSpan: 1 },
    { name: 'position', label: 'Position/Job Title', type: 'text', required: true, placeholder: 'Enter job position', colSpan: 1 },
    { name: 'experience', label: 'Experience', type: 'select', required: true, options: experienceOptions, colSpan: 1 },
    { name: 'location', label: 'Location', type: 'text', required: true, placeholder: 'Enter job location', colSpan: 1 },
    { name: 'salary', label: 'Salary', type: 'text', required: true, placeholder: 'e.g., ₹3,00,000 - ₹5,00,000 per annum', colSpan: 1 },
    { name: 'jobType', label: 'Job Type', type: 'select', required: true, options: jobTypeOptions, colSpan: 1 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Company Details Management
            </h1>
            <p className="text-gray-600 mt-1">Manage job postings and company information</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Total count badge */}
            <div className="px-3 py-2 bg-green-600 text-white rounded-md text-sm font-semibold">
              Total: {companyDetails.length}
            </div>
            <button
              onClick={() => {
                resetForm();
                setIsFormOpen(!isFormOpen);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-lg transition duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {isFormOpen ? 'Cancel' : 'Add New Job Posting'}
            </button>
          </div>
        </div>

        {/* Form Section */}
        {isFormOpen && (
          <div id="company-form" className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingId ? '✏️ Edit Job Posting' : '➕ Add New Job Posting'}
              </h2>
              {editingId && (
                <button
                  onClick={resetForm}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  Clear form
                </button>
              )}
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {formFields.map((field) => (
                  <div key={field.name} className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    
                    {field.type === 'select' ? (
                      <select
                        {...register(field.name, { required: field.required ? `${field.label} is required` : false })}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ${
                          errors[field.name] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select {field.label}</option>
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        {...register(field.name, { 
                          required: field.required ? `${field.label} is required` : false,
                        })}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ${
                          errors[field.name] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={field.placeholder}
                      />
                    )}
                    
                    {errors[field.name] && (
                      <p className="mt-1 text-xs text-red-600">{errors[field.name].message}</p>
                    )}
                  </div>
                ))}

                {/* Primary Phone */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    {...register('phone', { 
                      required: 'Phone number is required',
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: 'Please enter a valid 10-digit phone number',
                      }
                    })}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter primary phone number"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-xs text-red-600">{errors.phone.message}</p>
                  )}
                </div>

                {/* Primary Email */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'Email address is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      }
                    })}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter primary email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                  )}
                </div>

                {/* Alternate Phone with Plus Icon */}
                <div className="col-span-1">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Alternate Phone Number
                    </label>
                    {!showAltPhone && (
                      <button
                        type="button"
                        onClick={() => setShowAltPhone(true)}
                        className="text-green-600 hover:text-green-700 transition-colors"
                        title="Add alternate phone number"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {showAltPhone && (
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        {...register('alternatePhone', {
                          pattern: {
                            value: /^[0-9]{10}$/,
                            message: 'Please enter a valid 10-digit phone number',
                          }
                        })}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ${
                          errors.alternatePhone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter alternate phone number"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowAltPhone(false);
                          setValue('alternatePhone', '');
                        }}
                        className="text-red-600 hover:text-red-700"
                        title="Remove"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {errors.alternatePhone && (
                    <p className="mt-1 text-xs text-red-600">{errors.alternatePhone.message}</p>
                  )}
                </div>

                {/* Alternate Email with Plus Icon */}
                <div className="col-span-1">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Alternate Email Address
                    </label>
                    {!showAltEmail && (
                      <button
                        type="button"
                        onClick={() => setShowAltEmail(true)}
                        className="text-green-600 hover:text-green-700 transition-colors"
                        title="Add alternate email address"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {showAltEmail && (
                    <div className="flex gap-2">
                      <input
                        type="email"
                        {...register('alternateEmail', {
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          }
                        })}
                        className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 ${
                          errors.alternateEmail ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter alternate email address"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowAltEmail(false);
                          setValue('alternateEmail', '');
                        }}
                        className="text-red-600 hover:text-red-700"
                        title="Remove"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {errors.alternateEmail && (
                    <p className="mt-1 text-xs text-red-600">{errors.alternateEmail.message}</p>
                  )}
                </div>

                {/* Message Textarea */}
                <div className="col-span-1 md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Message / Notes
                  </label>
                  <textarea
                    {...register('message')}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                    placeholder="Add any additional information, requirements, or notes about this job posting..."
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {loading ? 'Processing...' : editingId ? 'Update Job Posting' : 'Create Job Posting'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Table Section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Experience
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Salary
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Type
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Person
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alternate Contacts
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-4 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && companyDetails.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-12 text-center">
                      <div className="flex justify-center items-center space-x-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="text-gray-500">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : companyDetails.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p>No job postings found</p>
                        <button
                          onClick={() => setIsFormOpen(true)}
                          className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Add your first job posting →
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((company, index) => {
                    const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                    return (
                      <tr key={company._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-4 py-2 text-sm text-gray-900 font-medium">{globalIndex}</td>
                        <td className="px-4 py-2 text-sm font-semibold text-gray-900 min-w-[150px]">
                          {company.companyName}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {company.position}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {company.experience}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {company.location}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 font-medium">
                          {company.salary}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            company.jobType === 'Full-time' ? 'bg-green-100 text-green-800' :
                            company.jobType === 'Part-time' ? 'bg-yellow-100 text-yellow-800' :
                            company.jobType === 'Remote' ? 'bg-purple-100 text-purple-800' :
                            company.jobType === 'Hybrid' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {company.jobType}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 font-medium">
                          {company.name}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <a href={`tel:${company.phone}`} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {company.phone}
                          </a>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <a href={`mailto:${company.email}`} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {company.email}
                          </a>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {(company.alternatePhone || company.alternateEmail) ? (
                            <div className="flex flex-col gap-1 text-xs">
                              {company.alternatePhone && (
                                <a href={`tel:${company.alternatePhone}`} className="text-green-600 hover:text-green-800 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                  {company.alternatePhone}
                                </a>
                              )}
                              {company.alternateEmail && (
                                <a href={`mailto:${company.alternateEmail}`} className="text-green-600 hover:text-green-800 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  {company.alternateEmail}
                                </a>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">No alternate contacts</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 max-w-xs">
                          {company.message ? (
                            <div className="truncate max-w-[200px]" title={company.message}>
                              💬 {company.message.length > 50 ? company.message.substring(0, 50) + '...' : company.message}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">No message</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium">
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleEdit(company)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Edit"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(company._id)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Delete"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Table Footer with pagination */}
          {companyDetails.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-3">
              <p className="text-sm text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, companyDetails.length)} of {companyDetails.length} job postings
              </p>
              <div className="flex items-center gap-3">
                <p className="text-sm font-semibold text-gray-700">
                  Page {currentPage} of {totalPages}
                </p>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          )}
          
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 transform transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Confirm Delete</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this job posting? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition font-medium"
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CompaniesDetail;